import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Rocket, Zap, Layers, Sparkles, ArrowRight, Shield, Globe, Terminal } from 'lucide-react';
import { YachtLogo } from '../components/YachtLogo';
import { motion } from 'framer-motion';
import Navbar from './Navbar';

export default function Subpage({ 
  title, 
  subtitle, 
  description, 
  features, 
  icon: Icon = Sparkles 
}: { 
  title: string, 
  subtitle: string, 
  description: string, 
  features: { title: string, description: string, icon: any }[],
  icon?: any
}) {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="px-8 py-24 md:py-32 flex flex-col items-center text-center max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-16 h-16 bg-[#001F3F] border border-[#0074D9]/30 rounded-2xl flex items-center justify-center text-[#0074D9] mb-8 shadow-[0_0_30px_rgba(0,116,217,0.2)]"
          >
            <Icon size={32} />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-sans font-bold tracking-tight mb-6 bg-gradient-to-b from-white to-zinc-500 bg-clip-text text-transparent"
          >
            {title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[10px] font-mono uppercase tracking-[0.4em] text-[#0074D9] mb-8"
          >
            {subtitle}
          </motion.p>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg md:text-xl text-zinc-400 font-light leading-relaxed mb-12"
          >
            {description}
          </motion.p>
          <Link 
            to="/signup" 
            className="px-8 py-4 bg-white text-black font-mono uppercase tracking-widest text-xs font-bold rounded-xl hover:bg-zinc-200 transition-all flex items-center gap-3"
          >
            Start Building Now <ArrowRight size={14} />
          </Link>
        </section>

        {/* Features */}
        <section className="px-8 py-24 bg-black/50 border-t border-white/5">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 bg-[#0A0A0A] border border-white/5 rounded-3xl hover:border-white/10 transition-colors group"
              >
                <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center text-zinc-400 mb-6 group-hover:text-white transition-colors">
                  <f.icon size={24} />
                </div>
                <h3 className="text-xl font-mono uppercase tracking-tight text-white mb-4">{f.title}</h3>
                <p className="text-zinc-500 font-light text-sm leading-relaxed">{f.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="px-8 py-32 flex flex-col items-center text-center">
          <h2 className="text-3xl md:text-4xl font-sans font-bold tracking-tight mb-8">
            Ready to accelerate your workflow?
          </h2>
          <div className="flex gap-4">
            <Link 
              to="/signup" 
              className="px-8 py-4 bg-[#0074D9] text-white font-mono uppercase tracking-widest text-xs font-bold rounded-xl hover:bg-[#0074D9]/80 transition-all"
            >
              Sign Up Free
            </Link>
            <Link 
              to="/pricing" 
              className="px-8 py-4 bg-zinc-900 text-white font-mono uppercase tracking-widest text-xs font-bold rounded-xl hover:bg-zinc-800 transition-all border border-white/10"
            >
              View Pricing
            </Link>
          </div>
        </section>
      </main>

      <footer className="px-8 py-12 border-t border-white/5 bg-black">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-3">
            <YachtLogo className="w-5 h-5" />
            <span className="font-mono text-sm tracking-tighter uppercase text-zinc-500">© 2026 Yacht Labs</span>
          </div>
          <div className="flex items-center gap-8 text-[9px] font-mono uppercase tracking-widest text-zinc-700">
            <Link to="/product" className="hover:text-zinc-400">Product</Link>
            <Link to="/solutions" className="hover:text-zinc-400">Solutions</Link>
            <Link to="/resources" className="hover:text-zinc-400">Resources</Link>
            <Link to="/about" className="hover:text-zinc-400">Company</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
